import { uuid } from '@atlaskit/adf-schema';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { mapSlice } from '@atlaskit/editor-common/utils';
import { type Slice } from '@atlaskit/editor-prosemirror/model';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { Attrs, Node, Fragment } from 'prosemirror-model';
import { EXTENSION_NAMESPACE } from '../utils/constants';

function getForgeLocalIdFromNode(node: Node): string | undefined {
	return node.attrs.extensionType === EXTENSION_NAMESPACE
		? node.attrs.parameters?.localId
		: undefined;
}

// When Forge extensions are copied, the attrs.localId should be the same as the attrs.parameters.localId.
// Otherwise, it won't be able to use the parameters.localId to locate the node (which is checked against by attrs.localId in editor-plugin-extension/src/pm-plugins/extension-api.ts).
function handleForgeExtensionLocalId({
	slice,
	filterForgeExtensionNode,
}: {
	filterForgeExtensionNode: (node: Node) => boolean;
	slice: Slice;
}): Slice {
	return mapSlice(slice, (node) => {
		if (filterForgeExtensionNode(node) && getForgeLocalIdFromNode(node) !== node.attrs.localId) {
			const attrs: Attrs = {
				...node.attrs,
				localId: getForgeLocalIdFromNode(node),
			};
			return node.type.create(attrs, node.content, node.marks);
		}
		return node;
	});
}

export const createUniqueLocalIdPlugin = (): SafePlugin =>
	new SafePlugin({
		key: new PluginKey('forgeExtensionUniqueLocalIdPlugin'),
		props: {
			transformPasted: (slice, view) => {
				const state = view.state;

				const { extension, bodiedExtension, inlineExtension, multiBodiedExtension } =
					state.schema.nodes;

				const extensionTypes = new Set([
					extension,
					bodiedExtension,
					inlineExtension,
					multiBodiedExtension,
				]);
				const forgeIdsObserved = new Set<string>();

				const iterateExtensions = (
					fragment: Fragment | Node,
					handler: (node: Node, pos: number) => void,
				) => {
					fragment.descendants((node, pos) => {
						if (!!node.type && extensionTypes.has(node.type)) {
							handler(node, pos);
							/**
							 * If it's a multiBodiedExtension or bodiedExtension, we'll need to keep digging;
							 * since we can have more extension nodes within the contents of that
							 */
							return [multiBodiedExtension, bodiedExtension].includes(node.type);
						}

						/**
						 * Otherwise continue traversing, we can encounter extensions nested in
						 * expands/bodiedExtensions
						 */
						return true;
					});
				};

				// Loop through document to see what IDs are already in use
				iterateExtensions(state.doc, (node) => {
					const forgeLocalId = getForgeLocalIdFromNode(node);
					if (forgeLocalId) {
						forgeIdsObserved.add(forgeLocalId);
					}
				});

				// Now, loop through the pasted content and generate new IDs for any that are already in use
				iterateExtensions(slice.content, (node) => {
					const forgeLocalId = getForgeLocalIdFromNode(node);
					if (!forgeLocalId) {
						return;
					}
					if (!forgeIdsObserved.has(forgeLocalId)) {
						forgeIdsObserved.add(forgeLocalId);
					} else {
						// attrs.localId will be updated in handleLocalId
						node.attrs.parameters.localId = uuid.generate();
					}
				});

				function filterForgeExtensionNode(node: Node): boolean {
					return !!node.type && extensionTypes.has(node.type) && !!getForgeLocalIdFromNode(node);
				}

				slice = handleForgeExtensionLocalId({ slice, filterForgeExtensionNode });

				return slice;
			},
		},
	});
